import React, { useEffect, useState } from 'react';
import { Configuration, FireplaceModel } from '../types';
import { generateAllOptions, OptionGroup, OptionItem } from '../utils/optionGenerator';
import { nemoSupportsBuffor, controllerRequiresDoorSensor } from '../data/controllerData';
import CornerHandleModal from './CornerHandleModal';

interface ConfiguratorOptionsProps {
  model: FireplaceModel | null;
  config: Configuration;
  onConfigChange: (key: keyof Configuration, value: any) => void;
  onSpecialConfigChange?: (key: keyof Configuration, value: any) => void;
  priceMode: 'netto' | 'vat8' | 'vat23';
}

const ConfiguratorOptions: React.FC<ConfiguratorOptionsProps> = ({
  model,
  config,
  onConfigChange,
  onSpecialConfigChange,
  priceMode
}) => {
  const [optionGroups, setOptionGroups] = useState<OptionGroup[]>([]);
  const [showCornerHandleModal, setShowCornerHandleModal] = useState(false);

  // Calculate price with VAT
  const calculatePriceWithVAT = (basePrice: number): number => {
    const vatMultiplier = priceMode === 'vat8' ? 1.08 : priceMode === 'vat23' ? 1.23 : 1;
    return Math.round(basePrice * vatMultiplier);
  };

  useEffect(() => {
    if (model) {
      const options = generateAllOptions(model, config, onConfigChange);
      setOptionGroups(options);

      // --- AUTO-SELECT GLASS IF ONLY ONE OPTION AND NOT SET ---
      const glassGroup = options.find(g => g.id === 'glass');
      if (glassGroup && glassGroup.options.length === 1 && !config.glass) {
        onConfigChange('glass', glassGroup.options[0].value);
      }
    } else {
      setOptionGroups([]);
    }
  }, [model, config, onConfigChange]);

  const handleOptionChange = (optionId: string, value: string | number | boolean) => {
    // Use special handler for front-variant if provided
    if (optionId === 'front-variant' && onSpecialConfigChange) {
      onSpecialConfigChange(optionId as keyof Configuration, value);
    } else {
      onConfigChange(optionId as keyof Configuration, value);
    }
  };

  const handleExclusiveOptionChange = (optionId: string, value: string | number, exclusiveWith?: string) => {
    // Jeśli włączamy opcję, która wyklucza inną - wyłącz tę inną
    if (value && exclusiveWith) {
      if (exclusiveWith === 'fusionCorner' && optionId === 'fusionBasic') {
        // Wyłączamy FUSION CORNER gdy włączamy FUSION
        onConfigChange('fusionCorner', 'none');
        onConfigChange('fusionCornerModules', 'none');
        onConfigChange('fusionCornerModulesCount', 0);
      } else if (exclusiveWith === 'fusion' && optionId === 'fusionCorner') {
        // Wyłączamy FUSION gdy włączamy FUSION CORNER
        onConfigChange('fusionBasic', 'none');
        onConfigChange('fusionModules', 'none');
        onConfigChange('fusionModulesCount', 0);
      }
    }
    
    // Zastosuj nową wartość
    handleOptionChange(optionId, value);
  };

  const renderOptionItem = (option: OptionItem, groupId: string, selectedValue?: string) => {
    // Special handling for NEMO Thermal Protection
    if (groupId === 'nemoThermalProtection') {
      const isChecked = config.nemoThermalProtection === 'with-protection';
      return (
        <label
          key={option.value}
          className={`option-item ${isChecked ? 'selected' : ''}`}
          style={{ position: 'relative' }}
        >
          <div style={{ flex: 1 }}>
            {option.description && (
              <div style={{ color: 'var(--muted)', fontSize: '14px', marginBottom: '12px' }}>
                {option.description}
              </div>
            )}
            {/* Checkbox for thermal protection */}
            <div style={{ marginTop: '12px', display: 'flex', gap: '8px', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '13px' }}>
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) => {
                    handleOptionChange('nemoThermalProtection', e.target.checked ? 'with-protection' : 'standard');
                  }}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontWeight: 'bold', color: '#666' }}>Tak</span>
              </label>
            </div>
          </div>
          {/* Price display in bottom-right corner */}
          {option.price && option.price > 0 && (
            <div style={{
              position: 'absolute',
              bottom: '12px',
              right: '12px',
              color: '#b89463',
              fontSize: '15px',
              fontWeight: 'bold'
            }}>
              +{calculatePriceWithVAT(option.price)} zł
            </div>
          )}
        </label>
      );
    }

    // Special handling for FUSION group
    if (groupId === 'fusion') {
      if (option.value === 'fusion') {
        const isSelected = config.fusionBasic === 'fusion';
        return (
          <label
            key={option.value}
            className={`option-item ${isSelected ? 'selected' : ''}`}
          >
            <input
              type="checkbox"
              name={`${groupId}_basic`}
              value={option.value}
              checked={isSelected}
              onChange={(e) => {
                handleExclusiveOptionChange('fusionBasic', e.target.checked ? 'fusion' : 'none', option.exclusiveWith);
                // Reset modules when unchecking FUSION
                if (!e.target.checked) {
                  handleOptionChange('fusionModules', 'none');
                  handleOptionChange('fusionModulesCount', 0);
                }
              }}
            />
            <div>
              <strong>{option.label}</strong>
              {option.description && (
                <div style={{ color: 'var(--muted)', fontSize: '14px' }}>
                  {option.description}
                </div>
              )}
              {/* Price display for FUSION */}
              {option.price && option.price > 0 && (
                <div className="option-price">
                  +{calculatePriceWithVAT(option.price)} zł
                </div>
              )}
            </div>
          </label>
        );
      } else if (option.value === 'fusionCorner') {
        const isSelected = config.fusionCorner === 'fusion-corner';
        const descriptionParts = option.description?.split(', Wymiary');
        return (
          <label
            key={option.value}
            className={`option-item ${isSelected ? 'selected' : ''}`}
          >
            <input
              type="checkbox"
              name={`${groupId}_corner`}
              value={option.value}
              checked={isSelected}
              onChange={(e) => {
                handleExclusiveOptionChange('fusionCorner', e.target.checked ? 'fusion-corner' : 'none', option.exclusiveWith);
                // Reset modules when unchecking FUSION CORNER
                if (!e.target.checked) {
                  handleOptionChange('fusionCornerModules', 'none');
                  handleOptionChange('fusionCornerModulesCount', 0);
                }
              }}
            />
            <div>
              <strong>{option.label}</strong>
              {option.description && (
                descriptionParts && descriptionParts.length === 2 ? (
                  <div className="option-description fusion-corner-description">
                    {descriptionParts[0]},
                    <span className="fusion-mobile-linebreak">Wymiary{descriptionParts[1]}</span>
                  </div>
                ) : (
                  <div className="option-description">
                    {option.description}
                  </div>
                )
              )}
              {/* Price display for FUSION CORNER */}
              {option.price && option.price > 0 && (
                <div className="option-price">
                  +{calculatePriceWithVAT(option.price)} zł
                </div>
              )}
            </div>
          </label>
        );
      } else if (option.value === 'fusionCornerModules') {
        const isFusionCornerSelected = config.fusionCorner === 'fusion-corner';
        const isModulesSelected = config.fusionCornerModules === 'fusion-corner-modules';
        const isDisabled = option.disabled || !isFusionCornerSelected;
        const unitPriceStyle: React.CSSProperties = isModulesSelected && !isDisabled
          ? { position: 'absolute', right: '8px', top: '48px' }
          : { position: 'absolute', right: '8px', bottom: '8px' };
        
        return (
          <label
            key={option.value}
            className={`option-item ${isModulesSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
          >
            <input
              type="checkbox"
              name={`${groupId}_corner_modules`}
              value="fusion-corner-modules"
              checked={isModulesSelected}
              disabled={isDisabled}
              onChange={(e) => {
                handleOptionChange('fusionCornerModules', e.target.checked ? 'fusion-corner-modules' : 'none');
                if (e.target.checked && config.fusionCornerModulesCount === 0) {
                  handleOptionChange('fusionCornerModulesCount', 1);
                } else if (!e.target.checked) {
                  handleOptionChange('fusionCornerModulesCount', 0);
                }
              }}
            />
            <div>
              <strong>{option.label}</strong>
              {option.description && (
                <div style={{ color: 'var(--muted)', fontSize: '14px' }}>
                  {option.description}
                </div>
              )}
              {/* Price display for FUSION CORNER modules */}
              {option.price && option.price > 0 && (
                <>
                  {isModulesSelected && !isDisabled && (
                    <div className="option-price-per-unit">
                      (razem: +{calculatePriceWithVAT(option.price * (config.fusionCornerModulesCount || 1))} zł)
                    </div>
                  )}
                  <div className="option-price-per-unit" style={unitPriceStyle}>
                    +{calculatePriceWithVAT(option.price)} zł/szt
                  </div>
                </>
              )}
              {option.hasCounter && isModulesSelected && !isDisabled && (
                <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      const currentValue = config.fusionCornerModulesCount || 1;
                      const newValue = Math.max(1, currentValue - 1);
                      handleOptionChange('fusionCornerModulesCount', newValue);
                    }}
                    style={{
                      width: '28px',
                      height: '28px',
                      border: '1px solid #ccc',
                      borderRadius: '3px',
                      background: '#f8f8f8',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      color: '#555'
                    }}
                  >
                    −
                  </button>
                  <input
                    type="text"
                    value={config.fusionCornerModulesCount || 1}
                    readOnly
                    style={{
                      width: '35px',
                      height: '28px',
                      border: '1px solid #ccc',
                      borderRadius: '3px',
                      textAlign: 'center',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      background: 'white',
                      color: '#333'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const currentValue = config.fusionCornerModulesCount || 1;
                      const newValue = Math.min(option.counterMax || 10, currentValue + 1);
                      handleOptionChange('fusionCornerModulesCount', newValue);
                    }}
                    style={{
                      width: '28px',
                      height: '28px',
                      border: '1px solid #ccc',
                      borderRadius: '3px',
                      background: '#f8f8f8',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      color: '#555'
                    }}
                  >
                    +
                  </button>
                  <span style={{ fontSize: '11px', color: '#666', marginLeft: '4px' }}>szt.</span>
                </div>
              )}
            </div>
          </label>
        );
      } else if (option.value === 'fusionModules') {
        const isFusionBasicSelected = config.fusionBasic === 'fusion';
        const isModulesSelected = config.fusionModules === 'fusion-modules';
        const isDisabled = option.disabled || !isFusionBasicSelected;
        const unitPriceStyle: React.CSSProperties = isModulesSelected && !isDisabled
          ? { position: 'absolute', right: '8px', top: '48px' }
          : { position: 'absolute', right: '8px', bottom: '8px' };
        
        return (
          <label
            key={option.value}
            className={`option-item ${isModulesSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
          >
            <input
              type="checkbox"
              name={`${groupId}_modules`}
              value="fusion-modules"
              checked={isModulesSelected}
              disabled={isDisabled}
              onChange={(e) => {
                handleOptionChange('fusionModules', e.target.checked ? 'fusion-modules' : 'none');
                if (e.target.checked && config.fusionModulesCount === 0) {
                  handleOptionChange('fusionModulesCount', 1);
                } else if (!e.target.checked) {
                  handleOptionChange('fusionModulesCount', 0);
                }
              }}
            />
            <div>
              <strong>{option.label}</strong>
              {option.description && (
                <div style={{ color: 'var(--muted)', fontSize: '14px' }}>
                  {option.description}
                </div>
              )}
              {/* Price display for FUSION modules */}
              {option.price && option.price > 0 && (
                <>
                  {isModulesSelected && !isDisabled && (
                    <div className="option-price-per-unit">
                      (razem: +{calculatePriceWithVAT(option.price * (config.fusionModulesCount || 1))} zł)
                    </div>
                  )}
                  <div className="option-price-per-unit" style={unitPriceStyle}>
                    +{calculatePriceWithVAT(option.price)} zł/szt
                  </div>
                </>
              )}
              {option.hasCounter && isModulesSelected && !isDisabled && (
                <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      const currentValue = config.fusionModulesCount || 1;
                      const newValue = Math.max(1, currentValue - 1);
                      handleOptionChange('fusionModulesCount', newValue);
                    }}
                    style={{
                      width: '28px',
                      height: '28px',
                      border: '1px solid #ccc',
                      borderRadius: '3px',
                      background: '#f8f8f8',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      color: '#555'
                    }}
                  >
                    −
                  </button>
                  <input
                    type="text"
                    value={config.fusionModulesCount || 1}
                    readOnly
                    style={{
                      width: '35px',
                      height: '28px',
                      border: '1px solid #ccc',
                      borderRadius: '3px',
                      textAlign: 'center',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      background: 'white',
                      color: '#333'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const currentValue = config.fusionModulesCount || 1;
                      const newValue = Math.min(option.counterMax || 10, currentValue + 1);
                      handleOptionChange('fusionModulesCount', newValue);
                    }}
                    style={{
                      width: '28px',
                      height: '28px',
                      border: '1px solid #ccc',
                      borderRadius: '3px',
                      background: '#f8f8f8',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      color: '#555'
                    }}
                  >
                    +
                  </button>
                  <span style={{ fontSize: '11px', color: '#666', marginLeft: '4px' }}>szt.</span>
                </div>
              )}
            </div>
          </label>
        );
      }
    }

    // Special handling for ACCUMULATION MATERIALS group
    if (groupId === 'accumulationMaterials') {
      if (option.value === 'aku-box') {
        const isSelected = config.akuBox === 'aku-box';
        return (
          <label
            key={option.value}
            className={`option-item ${option.disabled ? 'disabled' : ''} ${isSelected ? 'selected' : ''}`}
          >
            <input
              type="checkbox"
              checked={isSelected}
              disabled={option.disabled}
              onChange={(e) => {
                handleOptionChange('akuBox', e.target.checked ? 'aku-box' : 'none');
              }}
            />
            <div>
              <strong>{option.label}</strong>
              {option.description && <div className="option-description">{option.description}</div>}
              {/* Price display for AKU BOX */}
              {option.price && option.price > 0 && (
                <div className="option-price-top">
                  +{calculatePriceWithVAT(option.price)} zł
                </div>
              )}
            </div>
          </label>
        );
      } else if (option.value === 'easy-700') {
        const isSelected = config.easy700 === 'easy-700';
        return (
          <label
            key={option.value}
            className={`option-item ${option.disabled ? 'disabled' : ''} ${isSelected ? 'selected' : ''}`}
          >
            <input
              type="checkbox"
              checked={isSelected}
              disabled={option.disabled}
              onChange={(e) => {
                handleOptionChange('easy700', e.target.checked ? 'easy-700' : 'none');
              }}
            />
            <div>
              <strong>{option.label}</strong>
              {option.description && <div className="option-description">{option.description}</div>}
              {/* Price display for EASY 700 */}
              {option.price && option.price > 0 && (
                <div className="option-price-top">
                  +{calculatePriceWithVAT(option.price)} zł
                </div>
              )}
            </div>
          </label>
        );
      } else if (option.value === 'cma-n') {
        const isSelected = config.cmaN === 'cma-n';
        return (
          <label
            key={option.value}
            className={`option-item ${option.disabled ? 'disabled' : ''} ${isSelected ? 'selected' : ''}`}
          >
            <input
              type="checkbox"
              checked={isSelected}
              disabled={option.disabled}
              onChange={(e) => {
                if (e.target.checked) {
                  // Zaznaczenie CMA N - odznacz CMA S
                  handleOptionChange('cmaN', 'cma-n');
                  handleOptionChange('cmaS', 'none');
                } else {
                  handleOptionChange('cmaN', 'none');
                }
              }}
            />
            <div>
              <strong>{option.label}</strong>
              {option.description && <div className="option-description">{option.description}</div>}
              {option.price && option.price > 0 && (
                <div className="option-price-top">
                  +{calculatePriceWithVAT(option.price)} zł
                </div>
              )}
            </div>
          </label>
        );
      } else if (option.value === 'cma-s') {
        const isSelected = config.cmaS === 'cma-s';
        return (
          <label
            key={option.value}
            className={`option-item ${option.disabled ? 'disabled' : ''} ${isSelected ? 'selected' : ''}`}
          >
            <input
              type="checkbox"
              checked={isSelected}
              disabled={option.disabled}
              onChange={(e) => {
                if (e.target.checked) {
                  // Zaznaczenie CMA S - odznacz CMA N
                  handleOptionChange('cmaS', 'cma-s');
                  handleOptionChange('cmaN', 'none');
                } else {
                  handleOptionChange('cmaS', 'none');
                }
              }}
            />
            <div>
              <strong>{option.label}</strong>
              {option.description && <div className="option-description">{option.description}</div>}
              {option.price && option.price > 0 && (
                <div className="option-price-top">
                  +{calculatePriceWithVAT(option.price)} zł
                </div>
              )}
            </div>
          </label>
        );
      }
    }

    // Special handling for THERMAL MODULES group - similar to FUSION
    if (groupId === 'thermalModules') {
      if (option.value === 'thermal') {
        const isSelected = config.thermalModules === 'thermal';
        const unitPriceStyle: React.CSSProperties = isSelected
          ? { position: 'absolute', right: '8px', top: '48px' }
          : { position: 'absolute', right: '8px', bottom: '8px' };
        return (
          <label
            key={option.value}
            className={`option-item ${isSelected ? 'selected' : ''}`}
          >
            <input
              type="checkbox"
              name={`${groupId}_thermal`}
              value={option.value}
              checked={isSelected}
              onChange={(e) => {
                handleOptionChange('thermalModules', e.target.checked ? 'thermal' : 'none');
                // Initialize count when checking
                if (e.target.checked && config.thermalModulesCount === 0) {
                  handleOptionChange('thermalModulesCount', 1);
                } else if (!e.target.checked) {
                  handleOptionChange('thermalModulesCount', 0);
                }
              }}
            />
            <div>
              <strong>{option.label}</strong>
              {option.description && (
                <div style={{ color: 'var(--muted)', fontSize: '14px' }}>
                  {option.description}
                </div>
              )}
              {/* Price display for thermal modules */}
              {option.price && option.price > 0 && (
                <>
                  {isSelected && (() => {
                    const count = config.thermalModulesCount || 1;
                    // Extract weight from label - match pattern like (34,5 kg) or (21 kg)
                    const weightMatch = option.label.match(/\((\d+,?\d*)\s*kg/);
                    const singleWeight = weightMatch ? parseFloat(weightMatch[1].replace(',', '.')) : 0;
                    const totalWeight = singleWeight * count;
                    
                    return (
                      <div className="option-price-per-unit">
                        (razem: +{calculatePriceWithVAT(option.price * count)} zł, {totalWeight.toString().replace('.', ',')} kg)
                      </div>
                    );
                  })()}
                  <div className="option-price-per-unit" style={unitPriceStyle}>
                    +{calculatePriceWithVAT(option.price)} zł/szt
                  </div>
                </>
              )}
              {option.hasCounter && isSelected && (
                <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      const currentValue = config.thermalModulesCount || 1;
                      const newValue = Math.max(1, currentValue - 1);
                      handleOptionChange('thermalModulesCount', newValue);
                    }}
                    style={{
                      width: '28px',
                      height: '28px',
                      border: '1px solid #ccc',
                      borderRadius: '3px',
                      background: '#f8f8f8',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      color: '#555'
                    }}
                  >
                    −
                  </button>
                  <input
                    type="text"
                    value={config.thermalModulesCount || 1}
                    readOnly
                    style={{
                      width: '35px',
                      height: '28px',
                      border: '1px solid #ccc',
                      borderRadius: '3px',
                      textAlign: 'center',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      background: 'white',
                      color: '#333'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const currentValue = config.thermalModulesCount || 1;
                      const newValue = Math.min(option.counterMax || 10, currentValue + 1);
                      handleOptionChange('thermalModulesCount', newValue);
                    }}
                    style={{
                      width: '28px',
                      height: '28px',
                      border: '1px solid #ccc',
                      borderRadius: '3px',
                      background: '#f8f8f8',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      color: '#555'
                    }}
                  >
                    +
                  </button>
                </div>
              )}
            </div>
          </label>
        );
      }
      // Skip any other options in thermal modules group
      return null;
    }

    // Special handling for CONTROLLERS group
    if (groupId === 'controllers') {
      // Determine if this is a door sensor or regular controller
      const isDoorSensor = option.value === 'czujnik-drzwiczek';
      const isSelected = isDoorSensor 
        ? config.doorSensor === 'czujnik-drzwiczek'
        : config.controller === option.value;
      
      const isNemo = model?.id?.startsWith('nemo-') || false;
      const sensorRequired = controllerRequiresDoorSensor(config.controller, isNemo);
      
      // Door sensor logic:
      // - BEZ sterownika: dostępny do swobodnego wyboru
      // - Dragon + sterownik: zablokowany (automatycznie wymagany)
      // - NEMO GX: zablokowany (automatycznie wymagany)
      // - NEMO non-GX: niedostępny (grayed out)
      let isDoorSensorDisabled = false;
      let doorSensorDescription = option.description;
      
      if (isDoorSensor) {
        if (!config.controller || config.controller === 'none') {
          // BEZ sterownika - czujnik dostępny do swobodnego wyboru
          isDoorSensorDisabled = false;
        } else if (isNemo) {
          // Dla NEMO: zawsze disabled (wymagany dla GX, niedostępny dla innych)
          isDoorSensorDisabled = true;
        } else {
          // Dla Dragon: zablokowany (nie można odhaczyć) gdy wymagany
          isDoorSensorDisabled = sensorRequired;
        }
      }
      
      const isDisabled = isDoorSensor ? isDoorSensorDisabled : (option.disabled || false);
      
      const requiresColor = option.colorPreview === 'white-black';
      const selectedColor = config.controllerColor || 'none';
      
      return (
        <label
          key={option.value}
          className={`option-item ${isSelected ? 'selected' : ''} ${isDisabled && !isDoorSensor ? 'disabled' : ''}`}
          style={isDoorSensor && isDisabled ? { cursor: 'not-allowed' } : undefined}
        >
          <input
            type="checkbox"
            name={groupId}
            value={option.value}
            checked={isSelected}
            disabled={isDisabled}
            onChange={(e) => {
              if (isDoorSensor) {
                // Door sensor logic
                if (e.target.checked) {
                  handleOptionChange('doorSensor', 'czujnik-drzwiczek');
                } else {
                  // Nie można odhaczyć gdy disabled (wymagany lub niedostępny)
                  if (isDoorSensorDisabled) return;
                  handleOptionChange('doorSensor', 'none');
                }
              } else {
                // Regular controller logic
                if (e.target.checked) {
                  handleOptionChange('controller', option.value);
                  // Ustaw domyślny kolor dla sterowników z wyborem koloru
                  if (requiresColor && selectedColor === 'none') {
                    handleOptionChange('controllerColor', 'white');
                  }
                  
                  // Auto-zaznacz czujnik drzwiczek jeśli kontroler go wymaga
                  const requiresDoorSensor = controllerRequiresDoorSensor(option.value, isNemo);
                  if (requiresDoorSensor) {
                    handleOptionChange('doorSensor', 'czujnik-drzwiczek');
                  } else {
                    // Dla NEMO: wyczyść doorSensor jeśli nowy kontroler nie jest RT-08G X
                    if (isNemo && config.doorSensor === 'czujnik-drzwiczek') {
                      handleOptionChange('doorSensor', 'none');
                    }
                  }
                } else {
                  handleOptionChange('controller', 'none');
                  handleOptionChange('controllerColor', 'none');
                  handleOptionChange('controllerBuffor', false);
                  // Czyść też doorSensor gdy odznaczamy kontroler
                  if (config.doorSensor === 'czujnik-drzwiczek') {
                    handleOptionChange('doorSensor', 'none');
                  }
                }
              }
            }}
          />
          <div style={{ flex: 1 }}>
            <strong style={isDoorSensor && isDisabled ? { opacity: 0.6 } : undefined}>
              {option.label}
            </strong>
            {/* Show custom description for door sensor or default description */}
            {(isDoorSensor ? doorSensorDescription : option.description) && (
              <div style={{ 
                color: 'var(--muted)', 
                fontSize: '14px',
                opacity: isDoorSensor && isDisabled ? 0.6 : 1
              }}>
                {isDoorSensor ? doorSensorDescription : option.description}
              </div>
            )}
            {/* Price display - always show for door sensor, conditionally for controllers */}
            {option.price && option.price > 0 && (isDoorSensor || !isDisabled) && (
              <div className={option.value === 'rt-08-om-grafik-duo-integra-wb' ? 'option-price' : 'option-price-top'}>
                +{calculatePriceWithVAT(option.price)} zł
              </div>
            )}
            {/* Color selector for controllers with WHITE/BLACK option */}
            {requiresColor && isSelected && !isDoorSensor && (
              <div style={{ marginTop: '12px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#666' }}>Kolor:</span>
                <button
                  type="button"
                  onClick={() => handleOptionChange('controllerColor', 'white')}
                  style={{
                    padding: '6px 16px',
                    border: selectedColor === 'white' ? '2px solid #C4A574' : '1px solid #ccc',
                    borderRadius: '4px',
                    background: selectedColor === 'white' ? '#f8f8f8' : 'white',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: selectedColor === 'white' ? 'bold' : 'normal',
                    color: '#333'
                  }}
                >
                  WHITE
                </button>
                <button
                  type="button"
                  onClick={() => handleOptionChange('controllerColor', 'black')}
                  style={{
                    padding: '6px 16px',
                    border: selectedColor === 'black' ? '2px solid #C4A574' : '1px solid #ccc',
                    borderRadius: '4px',
                    background: selectedColor === 'black' ? '#f8f8f8' : 'white',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: selectedColor === 'black' ? 'bold' : 'normal',
                    color: '#333'
                  }}
                >
                  BLACK
                </button>
              </div>
            )}
            {/* BUFOR checkbox for NEMO controllers that support it */}
            {isSelected && !isDoorSensor && model?.id?.startsWith('nemo-') && nemoSupportsBuffor(option.value) && (
              <div style={{ marginTop: '12px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '13px' }}>
                  <input
                    type="checkbox"
                    checked={config.controllerBuffor === true}
                    onChange={(e) => handleOptionChange('controllerBuffor', e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  <span style={{ fontWeight: 'bold', color: '#666' }}>BUFOR</span>
                </label>
              </div>
            )}
          </div>
        </label>
      );
    }

    // Standard handling for other options
    let isSelected = option.value === selectedValue;
    let isDisabled = option.disabled;
    
    // Special handling for fusion-modules in mobile
    if (option.value === 'fusion-modules') {
      isSelected = config.fusionModules === 'fusion-modules';
      // Disable if fusion basic is not selected (same as desktop)
      if (config.fusionBasic !== 'fusion') {
        isDisabled = true;
      }
    }
    
    const isRequiredEmpty = !selectedValue && (
      // Standard groups
      groupId === 'variant' || groupId === 'glass' || groupId === 'ceramic' || groupId === 'handle' || groupId === 'handleSide' || groupId === 'bezel' || groupId === 'sideGlass' ||
      // Vis-a-vis front groups  
      groupId === 'front-variant' || groupId === 'front-handleSide' || groupId === 'front-handle' || groupId === 'front-bezel' ||
      // Vis-a-vis back groups
      groupId === 'back-variant' || groupId === 'back-handleSide' || groupId === 'back-handle' || groupId === 'back-bezel'
    );

    return (
      <label
        key={option.value}
        className={`option-item ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''} ${isRequiredEmpty ? 'required' : ''}`}
      >
        <input
          type={option.isCheckbox ? "checkbox" : "radio"}
          name={groupId}
          value={option.value}
          checked={isSelected}
          disabled={isDisabled}
          onChange={(e) => {
            // Special handling for handleSide in corner models - show modal
            if (groupId === 'handleSide' && (option.value === 'left' || option.value === 'right')) {
              // Find the group to check if it's a corner model
              const handleSideGroup = optionGroups.find(g => g.id === 'handleSide');
              if (handleSideGroup?.showSmallGlassOption) {
                setShowCornerHandleModal(true);
              }
              handleOptionChange(groupId, e.target.value);
            }
            // Special handling for fusion-modules
            else if (option.value === 'fusion-modules') {
              handleOptionChange('fusionModules', e.target.checked ? 'fusion-modules' : 'none');
              if (e.target.checked && config.fusionModulesCount === 0) {
                handleOptionChange('fusionModulesCount', 1);
              } else if (!e.target.checked) {
                handleOptionChange('fusionModulesCount', 0);
              }
            } else {
              // Standard handling for other options
              if (option.isCheckbox) {
                handleOptionChange(groupId, e.target.checked ? option.value : 'none');
              } else {
                handleOptionChange(groupId, e.target.value);
              }
            }
          }}
        />
        {option.colorPreview && (
          <div 
            className="color-preview" 
            style={{ background: option.colorPreview }}
          />
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingRight: '80px' }}>
          <div style={{ flex: 1 }}>
            <strong>{option.label}</strong>
            {option.description && (
              <div style={{ color: 'var(--muted)', fontSize: '14px' }}>
                {option.description}
              </div>
            )}
          </div>
          {/* Icon display - only on desktop for glass options */}
          {option.icon && (
            <img 
              src={option.icon} 
              alt={option.label}
              className="option-icon-desktop"
              style={{
                maxWidth: '32px',
                maxHeight: '32px',
                objectFit: 'contain',
                flexShrink: 0,
                marginLeft: '8px'
              }}
            />
          )}
          {/* Price display - show if price > 0 AND option is not disabled */}
          {option.price && option.price > 0 && !isDisabled && (
            <div className="option-price-top">
              +{calculatePriceWithVAT(option.price)} zł
            </div>
          )}
        </div>
          {/* Standard counter for other options */}
          {option.hasCounter && isSelected && option.value !== 'fusion-modules' && (
            <div style={{ marginTop: '8px' }}>
              <input
                type="number"
                min={option.counterMin || 1}
                max={option.counterMax || 10}
                value={config.fusionModulesCount || 1}
                onChange={(e) => handleOptionChange('fusionModulesCount', parseInt(e.target.value) || 1)}
                style={{
                  width: '60px',
                  padding: '4px',
                  border: '1px solid var(--line)',
                  borderRadius: '4px'
                }}
              />
              <span style={{ marginLeft: '8px', fontSize: '12px', color: 'var(--muted)' }}>
                szt.
              </span>
            </div>
          )}
      </label>
    );
  };

  const renderOptionGroup = (group: OptionGroup, index: number, allGroups: OptionGroup[]) => {
    // Skip invisible groups
    if (group.visible === false) {
      return null;
    }

    // Check if this is a vis-a-vis title that should be centered and uppercased
    const isVisAVisTitle = group.id.includes('front-') || group.id.includes('back-') || 
                          group.title.includes('przód') || group.title.includes('tył');

    // Check if this is an accessory group
    const isAccessoryGroup = ['akuPlus', 'fusion', 'accumulationMaterials', 'thermalModules', 'controllers'].includes(group.id);
    
    // Check if this is the first accessory group in the list (excluding akuPlus which appears at the top)
    const firstAccessoryIndex = allGroups.findIndex(g => ['fusion', 'accumulationMaterials', 'thermalModules', 'thermalHangers', 'controllers'].includes(g.id));
    const isFirstAccessoryGroup = isAccessoryGroup && index === firstAccessoryIndex;

    return (
      <div key={group.id}>
        {/* Add AKCESORIA header before first accessory section */}
        {isFirstAccessoryGroup && (
          <>
            <div style={{ 
              width: '100%',
              height: '6px',
              background: 'linear-gradient(to right, transparent, #475569, transparent)',
              marginTop: '40px',
              marginBottom: '30px'
            }} />
            <div style={{ 
              textAlign: 'center', 
              marginBottom: '30px', 
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#475569',
              textTransform: 'uppercase',
              letterSpacing: '2px'
            }}>
              AKCESORIA
            </div>
          </>
        )}
        
        <div className={`option-group ${group.disabled ? 'disabled' : ''}`}>
          {isVisAVisTitle ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0 }}>{group.title.toUpperCase()}</h3>
            </div>
          ) : (
            <>
              <h3>{group.title}</h3>
              {group.subtitle && (
                <div style={{ 
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: '-8px', 
                  marginBottom: '12px'
                }}>
                  <div style={{ 
                    fontSize: '13px', 
                    color: '#000', 
                    border: '2px solid var(--warning)',
                    background: 'rgba(255, 193, 7, 0.1)',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    width: 'auto',
                    display: 'inline-block',
                    fontStyle: 'italic'
                  }}>
                    {group.subtitle}
                  </div>
                </div>
              )}
            </>
          )}
          <div className="option-grid">
            {group.options.map(option => 
              renderOptionItem(
                { ...option, disabled: option.disabled || group.disabled }, 
                group.id, 
                config[group.id as keyof Configuration] as string
              )
            )}
          </div>
          

        </div>
      </div>
    );
  };

  if (!model) {
    return null;
  }

  return (
    <>
      <div className="options">
        {optionGroups.map((group, index) => renderOptionGroup(group, index, optionGroups))}
      </div>
      <CornerHandleModal 
        isOpen={showCornerHandleModal} 
        onClose={() => setShowCornerHandleModal(false)} 
      />
    </>
  );
};

export default ConfiguratorOptions;
